
import { useState, useEffect } from 'react';

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  status: string;
  assigned_agent: string;
  configuration: string;
  updated_at: string;
  assigned_user?: {
    id: string;
    name: string;
    email: string;
  };
  address?: string;
  comments?: string;
  m2_habitable?: string;
  systeme_chauffage?: string;
  installateur?: string;
  nombre_personnes?: string;
  categorie_revenu?: string;
  custom_fields?: any;
  rdv_date?: string;
  rdv_time?: string;
  rdv_notes?: string;
}

interface Status {
  id: string;
  label: string;
  color: string;
  order_index: number;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
}

interface PipelineProps {
  currentUser: {
    id: string;
    name: string;
    role: 'admin' | 'agent';
  };
}

export default function Pipeline({ currentUser }: PipelineProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draggedClient, setDraggedClient] = useState<Client | null>(null);

  // Charger les données depuis Supabase en temps réel
  const loadData = async () => {
    try {
      setLoading(true);
      
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      if (!currentUser.company_id) {
        setError('Société non trouvée');
        return;
      }

      // Charger clients avec company_id
      const clientsResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?action=list&company_id=${currentUser.company_id}`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        }
      });

      // Charger statuts avec company_id
      const statusesResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?action=statuses&company_id=${currentUser.company_id}`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        }
      });

      const clientsResult = await clientsResponse.json();
      const statusResult = await statusesResponse.json();

      // Charger les utilisateurs
      const usersResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management?action=list`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        }
      });
      const usersResult = await usersResponse.json();

      if (clientsResult.success) {
        setClients(clientsResult.clients || []);
      }

      if (statusResult.success) {
        setStatuses(statusResult.statuses || []);
      }

      if (usersResult.success) {
        setUsers(usersResult.users || []);
      }
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  // Nouvelle fonction pour mettre à jour le statut d'un client
  const updateClientStatus = async (clientId: string, newStatus: string) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      const client = clients.find(c => c.id === clientId);
      
      if (!client) return;

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${currentUser.company_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: clientId,
          firstName: client.first_name,
          lastName: client.last_name,
          phone: client.phone,
          email: client.email,
          address: client.address,
          status: newStatus,
          assignedAgent: client.assigned_agent,
          configuration: client.configuration,
          comments: client.comments,
          m2Habitable: client.m2_habitable,
          systemeChauffage: client.systeme_chauffage,
          installateur: client.installateur,
          nombrePersonnes: client.nombre_personnes,
          categorieRevenu: client.categorie_revenu,
          rdvDate: client.rdv_date,
          rdvTime: client.rdv_time,
          rdvNotes: client.rdv_notes
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erreur HTTP:', response.status, errorText);
        throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      if (result.success) {
        await loadData();
      } else {
        console.error('Erreur dans la réponse:', result);
        alert('Erreur lors de la mise à jour: ' + (result.error || 'Erreur inconnue'));
      }
    } catch (error) {
      console.error('Erreur détaillée:', error);
      alert('Erreur de connexion au serveur. Veuillez vérifier votre connexion et réessayer.');
    }
  };

  useEffect(() => {
    // Charger les données au démarrage
    loadData();

    // Actualiser les données toutes les 60 secondes (1 minute) pour la synchronisation optimisée
    const interval = setInterval(loadData, 60000);

    return () => clearInterval(interval);
  }, []);

  // Filtrer les clients selon le rôle
  const visibleClients = currentUser.role === 'admin' 
    ? clients 
    : clients.filter(client => client.assigned_agent === currentUser.id);

  // Organiser les clients par statut
  const clientsByStatus = statuses.reduce((acc, status) => {
    acc[status.id] = visibleClients.filter(client => client.status === status.id);
    return acc;
  }, {} as Record<string, Client[]>);

  // Gérer le drag and drop
  const handleDragStart = (client: Client) => {
    setDraggedClient(client);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    
    if (!draggedClient || draggedClient.status === newStatus) {
      setDraggedClient(null);
      return;
    }

    // Utiliser la nouvelle fonction de mise à jour
    await updateClientStatus(draggedClient.id, newStatus);
    setDraggedClient(null);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Chargement du pipeline...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pipeline des ventes</h1>
          <div className="flex items-center space-x-2">
            <p className="text-gray-600">{visibleClients.length} clients au total</p>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1"></div>
              <span className="text-xs text-green-600">Sync 1 min</span>
            </div>
          </div>
        </div>
      </div>

      {/* Message d'information amélioré */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <i className="ri-drag-drop-line text-green-600 mr-2"></i>
            <div>
              <p className="text-green-800 text-sm font-semibold">
                🎯 Pipeline synchronisé avec Supabase !
              </p>
              <p className="text-green-700 text-xs">
                Glissez-déposez les clients entre les colonnes - Sauvegarde automatique en temps réel
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1"></div>
              <span className="text-xs text-green-600">Sync 1 min</span>
            </div>
            <div className="text-green-600 font-mono text-xs">
              {statuses.length} statuts actifs
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statuses
          .sort((a, b) => a.order_index - b.order_index)
          .map((status) => {
            const statusClients = clientsByStatus[status.id] || [];
            
            return (
              <div 
                key={status.id}
                className="bg-white rounded-lg shadow-sm border overflow-hidden"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, status.id)}
              >
                {/* Column header */}
                <div 
                  className="p-4 text-white font-semibold text-sm"
                  style={{ backgroundColor: status.color }}
                >
                  <div className="flex items-center justify-between">
                    <span>{status.label}</span>
                    <span className="bg-white bg-opacity-30 rounded-full px-2 py-1 text-xs font-bold">
                      {statusClients.length}
                    </span>
                  </div>
                </div>

                {/* Client cards */}
                <div className="p-2 space-y-2 min-h-[400px]">
                  {statusClients.map((client) => {
                    const agent = users.find(u => u.id === client.assigned_agent) || client.assigned_user;
                    
                    return (
                      <div
                        key={client.id}
                        draggable
                        onDragStart={() => handleDragStart(client)}
                        className="bg-gray-50 rounded-lg p-3 cursor-move hover:bg-gray-100 transition-colors border border-gray-200 hover:border-gray-300"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {client.first_name} {client.last_name}
                            </p>
                            <p className="text-xs text-gray-500">{client.phone}</p>
                          </div>
                          <i className="ri-drag-move-2-line text-gray-400 text-sm"></i>
                        </div>

                        {client.configuration && (
                          <div className="mb-2">
                            <p className="text-xs text-gray-600 bg-gray-200 rounded px-2 py-1 inline-block">
                              {client.configuration}
                            </p>
                          </div>
                        )}

                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>
                            {new Date(client.updated_at).toLocaleDateString('fr-FR')}
                          </span>
                          {currentUser.role === 'admin' && agent && (
                            <span title={`Agent: ${agent.name}`}>
                              <i className="ri-user-line mr-1"></i>
                              {agent.name.split(' ')[0]}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {statusClients.length === 0 && (
                    <div className="text-center text-gray-400 py-8">
                      <i className="ri-inbox-line text-2xl mb-2 block"></i>
                      <p className="text-xs">Aucun client</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {/* Statistics summary */}
      <div className="mt-6 bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Résumé du pipeline</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {clientsByStatus['prospect']?.length || 0}
            </div>
            <p className="text-sm text-gray-600">Nouveaux prospects</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {(clientsByStatus['en-cours']?.length || 0) + (clientsByStatus['rdv-fixe']?.length || 0)}
            </div>
            <p className="text-sm text-gray-600">En négociation</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {clientsByStatus['termine']?.length || 0}
            </div>
            <p className="text-sm text-gray-600">Ventes concluées</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600">
              {visibleClients.length > 0 
                ? Math.round(((clientsByStatus['termine']?.length || 0) / visibleClients.length) * 100)
                : 0
              }%
            </div>
            <p className="text-sm text-gray-600">Taux de conversion</p>
          </div>
        </div>
      </div>
    </div>
  );
}
